import { components } from "@/api-schema";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { BranchTable } from "@/components/data-table/predefined/branch-table";
import {
  DataTableHeightMode,
  ImplementedAccessorColumnDef,
  ImplementedColumnDef,
  ImplementedDisplayColumnDef,
} from "@/components/data-table/types";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { CompanyFilter } from "@/components/filter/predefined/company-filter";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyValue } from "@/components/ui/empty-value";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { BuildingIcon, CheckCircle2, Circle } from "lucide-react";
import { FormEvent, ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type Branch = components["schemas"]["Branch"] & RowWithIdentifier;
type BranchCompany = Branch["company"];
type BranchAddress = Branch["address"];

interface VehicleValuationV1GewaSyncDialogProps {
  open: boolean;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (branchUuid: string) => Promise<boolean>;
}

export function VehicleValuationV1GewaSyncDialog({
  open,
  isPending,
  onOpenChange,
  onSubmit,
}: VehicleValuationV1GewaSyncDialogProps): ReactElement {
  const { t } = useTranslation(["common", "fab", "fit", "masterdata"]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const branchFilters = useMemo(() => [new CompanyFilter(), new BrandFilter()], []);
  const selectedBranchUuid = selectedBranch?.uuid ?? "";
  const selectedBranchTitle = selectedBranch ? getBranchDisplayLabel(selectedBranch) : "";
  const selectedBranchDetail = selectedBranch ? getBranchDetail(selectedBranch, selectedBranchTitle) : "";
  const branchColumns = useMemo(() => createBranchSelectionColumns(selectedBranchUuid, t), [selectedBranchUuid, t]);

  const resetSelection = (): void => {
    setSelectedBranch(null);
  };

  const handleOpenChange = (isOpen: boolean): void => {
    if (!isOpen) {
      resetSelection();
    }

    onOpenChange(isOpen);
  };

  const handleRowClick = (branch: Branch): void => {
    setSelectedBranch(branch);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (!selectedBranchUuid || isPending) {
      return;
    }

    const didSubmit = await onSubmit(selectedBranchUuid);
    if (didSubmit) {
      handleOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-4xl lg:max-w-5xl">
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-col gap-3">
          <DialogHeader>
            <DialogTitle>{t("fab:VehicleValuationV1GewaSyncDialogTitle")}</DialogTitle>
            <DialogDescription className="max-w-3xl text-sm">
              {t("fab:VehicleValuationV1GewaSyncDialogDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0">
            <BranchTable
              name="vehicle-valuation-v1-gewa-sync-branch-table"
              columns={branchColumns}
              heightMode={DataTableHeightMode.Custom}
              className="h-[min(46vh,28rem)]"
              showCreateButton={false}
              customRowActions={null}
              features={{ expansion: false, multiSelection: false, pagination: true, search: true }}
              filters={branchFilters}
              body={{ v1IdRequired: true, companyV1IdRequired: true }}
              onRowClick={handleRowClick}
              subComponent={null}
              disableUrlPersistence
              noDataIcon={BuildingIcon}
              noDataTitle={t("fit:NoBranchesAvailable")}
              noDataDescription={t("fit:NoDestinationBranchesFound")}
              noResultsTitle={t("fit:NoBranchFound")}
              noResultsDescription={t("common:NoBranchesFound")}
            />
          </div>

          <div className="min-h-18">
            <div
              aria-hidden={!selectedBranch}
              className={`bg-muted/30 rounded-md border p-3 transition-opacity ${selectedBranch ? "opacity-100" : "pointer-events-none opacity-0"}`}
            >
              {selectedBranch ? (
                <>
                  <div className="text-sm font-medium">{selectedBranchTitle || <EmptyValue />}</div>
                  {selectedBranchDetail ? (
                    <div className="text-muted-foreground mt-1 text-xs">{selectedBranchDetail}</div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant={ButtonVariant.Outlined}
                colorVariant={ButtonColorVariant.Secondary}
                disabled={isPending}
              >
                {t("common:Cancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!selectedBranchUuid || isPending}>
              {isPending ? <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor /> : null}
              {t("fab:VehicleValuationV1GewaSyncSubmit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function createBranchSelectionColumns(
  selectedBranchUuid: string,
  t: ReturnType<typeof useTranslation>["t"],
): ImplementedColumnDef<Branch, unknown>[] {
  const columns = [
    {
      id: "selected",
      size: 44,
      minSize: 44,
      maxSize: 44,
      enableSorting: false,
      header: () => <span className="sr-only">{t("common:Select")}</span>,
      cell: ({ row }) => {
        const isSelected = row.original.uuid === selectedBranchUuid;
        const Icon = isSelected ? CheckCircle2 : Circle;

        return (
          <Icon
            data-branch-selected={isSelected ? "true" : undefined}
            className={isSelected ? "text-primary h-4 w-4" : "text-muted-foreground/50 h-4 w-4"}
          />
        );
      },
      align: "center",
    } as ImplementedDisplayColumnDef<Branch, unknown>,
    {
      id: "name",
      size: 220,
      enableSorting: true,
      header: () => t("common:Name"),
      accessorFn: (row: Branch) => getBranchDisplayLabel(row),
      cell: (row) => {
        const name = row.getValue() as string | undefined;
        return <span className="font-medium">{name || <EmptyValue />}</span>;
      },
    } as ImplementedAccessorColumnDef<Branch, string | undefined>,
    {
      id: "company",
      size: 180,
      enableSorting: true,
      header: () => t("common:Company"),
      accessorFn: (row: Branch) => row.company,
      cell: (row) => {
        const company = row.getValue() as BranchCompany;
        return <span className="truncate">{company?.name ?? <EmptyValue />}</span>;
      },
    } as ImplementedAccessorColumnDef<Branch, BranchCompany>,
    {
      id: "address",
      size: 220,
      enableSorting: true,
      header: () => t("masterdata:Address"),
      accessorFn: (row: Branch) => row.address,
      cell: (row) => {
        const address = row.getValue() as BranchAddress | null;
        return <span className="truncate">{formatCompactAddress(address)}</span>;
      },
    } as ImplementedAccessorColumnDef<Branch, BranchAddress>,
  ] as unknown as ImplementedColumnDef<Branch, unknown>[];

  return columns;
}

function getBranchDisplayLabel(branch: Branch): string {
  return branch.name.trim();
}

function getBranchDetail(branch: Branch, title: string): string {
  const titleParts = getComparableTitleParts(title);
  const addressCity = branch.address?.city;
  const postalCodeAndCity = titleIncludesPart(titleParts, addressCity)
    ? branch.address?.postalCode
    : [branch.address?.postalCode, addressCity].filter(Boolean).join(" ");

  return uniqueTextParts([
    branch.company?.name,
    [branch.address?.street, branch.address?.houseNumber].filter(Boolean).join(" "),
    postalCodeAndCity,
  ])
    .filter((part) => !titleIncludesPart(titleParts, part))
    .filter(Boolean)
    .join(" · ");
}

function formatCompactAddress(address?: BranchAddress | null): string {
  return uniqueTextParts([[address?.postalCode, address?.city].filter(Boolean).join(" "), address?.street]).join(" · ");
}

function getComparableTitleParts(title: string): Set<string> {
  return new Set(title.split(" · ").map(normalizeComparableText).filter(Boolean));
}

function titleIncludesPart(titleParts: Set<string>, part?: string | null): boolean {
  const normalizedPart = normalizeComparableText(part);
  return normalizedPart ? titleParts.has(normalizedPart) : false;
}

function normalizeComparableText(part?: string | null): string {
  return part?.trim().toLocaleLowerCase() ?? "";
}

function uniqueTextParts(parts: Array<string | null | undefined>): string[] {
  const seenParts = new Set<string>();
  const uniqueParts: string[] = [];

  for (const part of parts) {
    const normalizedPart = part?.trim();

    if (!normalizedPart) {
      continue;
    }

    const key = normalizeComparableText(normalizedPart);

    if (seenParts.has(key)) {
      continue;
    }

    seenParts.add(key);
    uniqueParts.push(normalizedPart);
  }

  return uniqueParts;
}
